<script setup>
import { defineAsyncComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAnalytics } from '@/shared/analytics'
import { useBradleyEnvironment } from '@/shared/composables/use-bradley-environment.js'
import { BRADLEY_ANALYTICS_EVENTS } from '../analytics/bradley-analytics.constants.js'
import { useBradleySidebarPersistence } from '../composables/use-bradley-sidebar-persistence.js'
import BradleySidebarFooter from './bradley-sidebar-footer.vue'
import BradleySidebarRecents from './bradley-sidebar-recents.vue'

defineProps({
  conversations: {
    type: Array,
    default: () => [],
  },
  loadingConversations: {
    type: Boolean,
    default: false,
  },
  activeConversationId: {
    type: String,
    default: null,
  },
  activeProjectId: {
    type: String,
    default: null,
  },
  autoEditProjectId: {
    type: String,
    default: null,
  },
})

const emit = defineEmits([
  'selectTool',
  'openSettings',
  'newChat',
  'sidebarWidthChange',
  'openUserManual',
  'selectConversation',
  'renameConversation',
  'renameConversationInitiated',
  'deleteConversationInitiated',
  'deleteConversationCanceled',
  'removeConversation',
  'selectProject',
  'createProject',
  'renameProject',
  'archiveProject',
  'autoEditConsumed',
])

const { projectsEnabled: PROJECTS_ENABLED } = useBradleyEnvironment()
const BradleySidebarProjects = PROJECTS_ENABLED
  ? defineAsyncComponent(() => import('./bradley-sidebar-projects.vue'))
  : null

const { t } = useI18n()
const { trackEvent } = useAnalytics()

const SIDEBAR_COLLAPSED_WIDTH = 72
const SIDEBAR_EXPANDED_WIDTH = 240
const HEADER_HEIGHT = 86

const { initialExpanded, isFirstVisit, persistExpanded, markVisited } = useBradleySidebarPersistence()

const sidebarExpanded = ref(initialExpanded)
const activeMenuItem = ref('document')
const isScrolled = ref(false)
const sidebarRecentsReference = ref(null)
const sidebarProjectsReference = ref(null)
const recentsSectionOpen = ref(false)
const projectsSectionOpen = ref(false)
const footerMoreMenuOpen = ref(false)

const sidebarMenuGroups = ref([
  {
    items: [
      { id: 'new-chat', icon: 'pi pi-pen-to-square', label: () => t('newBradley.sidebar.newChat') },
    ],
  },
])

watch(sidebarExpanded, (expanded) => {
  if (!expanded) {
    recentsSectionOpen.value = false
    projectsSectionOpen.value = false
    footerMoreMenuOpen.value = false
  }
})

function toggleSidebar() {
  const previousState = sidebarExpanded.value ? 'expanded' : 'collapsed'
  const wasCollapsed = !sidebarExpanded.value
  sidebarExpanded.value = !sidebarExpanded.value
  const newState = sidebarExpanded.value ? 'expanded' : 'collapsed'
  persistExpanded(sidebarExpanded.value)
  trackEvent(BRADLEY_ANALYTICS_EVENTS.MENU_TOGGLED, {
    previous_state: previousState,
    new_state: newState,
  })
  if (wasCollapsed && sidebarExpanded.value) {
    trackEvent(BRADLEY_ANALYTICS_EVENTS.EXPAND_MENU, { source: 'sidebar_toggle' })
  }
  emitSidebarWidth()
}

function emitSidebarWidth() {
  const width = sidebarExpanded.value ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH
  emit('sidebarWidthChange', width)
}

function handleScroll() {
  isScrolled.value = window.scrollY > HEADER_HEIGHT
}

onMounted(() => {
  emitSidebarWidth()
  trackEvent(BRADLEY_ANALYTICS_EVENTS.MENU_OPENED, {
    menu_state: sidebarExpanded.value ? 'expanded' : 'collapsed',
    is_first_access: isFirstVisit,
  })
  markVisited()
  window.addEventListener('scroll', handleScroll)
  document.addEventListener('click', closeActionsMenu)
  handleScroll()
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
  document.removeEventListener('click', closeActionsMenu)
})

function handleMenuItemClick(item) {
  if (item.action) {
    item.action()
  }
  else {
    activeMenuItem.value = item.id

    if (item.id === 'new-chat') {
      trackEvent(BRADLEY_ANALYTICS_EVENTS.NEW_CHAT, { source: 'sidebar_menu' })
      emit('newChat')
    }
    else if (item.id === 'settings') {
      emit('openSettings')
    }
    else if (item.id === 'user-manual') {
      emit('openUserManual')
    }
    else if (item.label || item.toolValue) {
      const valueToEmit = item.toolValue ?? (typeof item.label === 'function' ? item.label() : item.label)
      emit('selectTool', valueToEmit)
    }
  }
}

function getMenuItemIcon(item) {
  return item.icon
}

function closeActionsMenu() {
  sidebarRecentsReference.value?.closeChatActionsMenu?.()
  sidebarProjectsReference.value?.closeProjectActionsMenu?.()
  footerMoreMenuOpen.value = false
}

function onFooterMenuOpen() {
  sidebarRecentsReference.value?.closeChatActionsMenu?.()
}

function onFooterOpenSettings() {
  activeMenuItem.value = 'settings'
  emit('openSettings')
}

function onFooterOpenUserManual() {
  activeMenuItem.value = 'user-manual'
  emit('openUserManual')
}

function onChatActionsOpened() {
  footerMoreMenuOpen.value = false
  sidebarProjectsReference.value?.closeProjectActionsMenu?.()
}

function onProjectActionsOpened() {
  footerMoreMenuOpen.value = false
  sidebarRecentsReference.value?.closeChatActionsMenu?.()
}

function handleCreateProjectClick() {
  activeMenuItem.value = 'projects'
  projectsSectionOpen.value = true
  recentsSectionOpen.value = false
  if (!sidebarExpanded.value) {
    sidebarExpanded.value = true
    persistExpanded(true)
    emitSidebarWidth()
  }
  emit('createProject')
}

function toggleExpandableSection(menuItemId, sectionOpenReference, otherSectionOpenReference) {
  activeMenuItem.value = menuItemId

  if (!sidebarExpanded.value) {
    toggleSidebar()
    sectionOpenReference.value = true
    otherSectionOpenReference.value = false
    return
  }

  sectionOpenReference.value = !sectionOpenReference.value
  if (sectionOpenReference.value) {
    otherSectionOpenReference.value = false
  }
}

function toggleRecentsSection() {
  toggleExpandableSection('recents', recentsSectionOpen, projectsSectionOpen)
}

function toggleProjectsSection() {
  toggleExpandableSection('projects', projectsSectionOpen, recentsSectionOpen)
}

defineExpose({
  prependProject: projectItem => sidebarProjectsReference.value?.prependProject?.(projectItem),
  syncProjectTitle: (projectId, title) => sidebarProjectsReference.value?.syncProjectTitle?.(projectId, title),
  removeProject: projectId => sidebarProjectsReference.value?.removeProject?.(projectId),
  reloadProjects: () => sidebarProjectsReference.value?.reloadProjects?.(),
  getProjectsCount: () => sidebarProjectsReference.value?.getProjectsCount?.() ?? 0,
})
</script>

<template>
  <div
    class="sidebar-menu"
    :class="{
      'sidebar-expanded': sidebarExpanded,
      'sidebar-scrolled': isScrolled,
      'sidebar-settings-flyout-open': footerMoreMenuOpen && !sidebarExpanded,
    }"
  >
    <div class="sidebar-content">
      <header class="sidebar-menu-header">
        <div class="sidebar-menu-toolbar">
          <button
            type="button"
            class="sidebar-menu-toolbar__toggle"
            :aria-expanded="sidebarExpanded"
            :aria-label="sidebarExpanded ? t('newBradley.sidebar.collapseMenu') : t('newBradley.sidebar.expandMenu')"
            :title="sidebarExpanded ? t('newBradley.sidebar.collapseMenu') : t('newBradley.sidebar.expandMenu')"
            @click="toggleSidebar()"
          >
            <span
              class="sidebar-menu-toolbar__toggle-box"
              aria-hidden="true"
            >
              <i class="pi pi-bars" />
            </span>
          </button>
        </div>
        <div
          class="menu-divider sidebar-menu-header__divider"
          aria-hidden="true"
        />
      </header>

      <div
        v-for="(group, groupIndex) in sidebarMenuGroups.slice(0, 1)"
        :key="`top-${groupIndex}`"
        class="menu-group menu-group--top"
      >
        <button
          v-for="item in group.items"
          :key="item.id"
          class="sidebar-menu-item"
          :class="{ active: activeMenuItem === item.id }"
          :title="typeof item.label === 'function' ? item.label() : item.label"
          @click="handleMenuItemClick(item)"
        >
          <i :class="getMenuItemIcon(item)" />
          <span
            v-if="sidebarExpanded"
            class="sidebar-label"
          >{{ typeof item.label === 'function' ? item.label() : item.label }}</span>
          <span
            v-if="sidebarExpanded && item.trailingIcon"
            class="sidebar-menu-item__trailing"
          >
            <i :class="item.trailingIcon" />
          </span>
        </button>
      </div>

      <bradley-sidebar-projects
        v-if="PROJECTS_ENABLED"
        ref="sidebarProjectsReference"
        :sidebar-expanded="sidebarExpanded"
        :section-open="projectsSectionOpen"
        :projects-nav-active="activeMenuItem === 'projects'"
        :active-project-id="activeProjectId"
        :auto-edit-project-id="autoEditProjectId"
        @projects-trigger="toggleProjectsSection"
        @select-project="emit('selectProject', $event)"
        @create-project="handleCreateProjectClick"
        @rename-project="emit('renameProject', $event)"
        @archive-project="emit('archiveProject', $event)"
        @auto-edit-consumed="emit('autoEditConsumed')"
        @project-actions-opened="onProjectActionsOpened"
      />

      <bradley-sidebar-recents
        ref="sidebarRecentsReference"
        :conversations="conversations"
        :loading-conversations="loadingConversations"
        :active-conversation-id="activeConversationId"
        :sidebar-expanded="sidebarExpanded"
        :section-open="recentsSectionOpen"
        :recents-nav-active="activeMenuItem === 'recents'"
        @recents-trigger="toggleRecentsSection"
        @select-conversation="emit('selectConversation', $event)"
        @rename-conversation-initiated="emit('renameConversationInitiated', $event)"
        @rename-conversation="emit('renameConversation', $event)"
        @delete-conversation-initiated="emit('deleteConversationInitiated', $event)"
        @delete-conversation-canceled="emit('deleteConversationCanceled', $event)"
        @remove-conversation="emit('removeConversation', $event)"
        @chat-actions-opened="onChatActionsOpened"
      />

      <bradley-sidebar-footer
        v-model:footer-menu-open="footerMoreMenuOpen"
        :sidebar-expanded="sidebarExpanded"
        @footer-menu-open="onFooterMenuOpen"
        @open-settings="onFooterOpenSettings"
        @open-user-manual="onFooterOpenUserManual"
      />
    </div>
  </div>
</template>

<style scoped>
.sidebar-menu {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: #fafafe;
  border-left: 1px solid #e5e7eb;
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.05);
  transition: width 0.3s ease;
  overflow: hidden;
  overflow-x: hidden;
  box-sizing: border-box;
}

.sidebar-content {
  flex: 1 1 auto;
  min-height: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.menu-group {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.menu-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 0.5rem 0;
}

.sidebar-menu-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: transparent;
  border: none;
  border-right: 3px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #4b5563;
  font-size: 1rem;
  position: relative;
  text-align: left;
}

.sidebar-menu-item:hover {
  background: #e5e7eb;
  color: #1f2937;
}

.sidebar-menu-item.active {
  border-right-color: var(--color-dss-primary-500);
  color: var(--color-dss-primary-500);
  background: #eff6ff;
}

.sidebar-menu-item i {
  flex-shrink: 0;
  width: 20px;
  text-align: center;
  font-size: 1.125rem;
  color: var(--color-dss-primary-500);
}

.sidebar-menu-item:hover i,
.sidebar-menu-item.active i {
  color: var(--color-dss-primary-500);
}

.sidebar-menu-item__trailing {
  margin-left: auto;
  color: var(--color-dss-primary-500);
}

.sidebar-menu-item__trailing i {
  width: auto;
  font-size: 0.875rem;
}

.sidebar-label {
  font-family: var(--font-inter, 'Inter', sans-serif);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.005em;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.2s ease;
  color: #374151;
}

.sidebar-expanded .sidebar-label {
  opacity: 1;
}

.menu-group--top .sidebar-menu-item {
  font-weight: 500;
  padding: 1rem;
}

.sidebar-menu-header {
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  box-sizing: border-box;
  height: 67px;
}

.sidebar-menu-header .sidebar-menu-toolbar {
  display: flex;
  flex: 1 1 auto;
  flex-direction: row;
  min-height: 0;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  padding: 0 1rem;
  box-sizing: border-box;
}

.sidebar-menu-header__divider {
  flex-shrink: 0;
  margin: 0;
}

.sidebar-menu-toolbar__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.125rem;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.sidebar-menu-toolbar__toggle:hover {
  background: transparent;
}

.sidebar-menu-toolbar__toggle-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border: 1px solid #9999eb;
  border-radius: 0.375rem;
  background: transparent;
  color: #0000cc;
  transition: border-color 0.2s ease, color 0.2s ease;
}

.sidebar-menu-toolbar__toggle:hover .sidebar-menu-toolbar__toggle-box {
  background: transparent;
  border-color: #9999eb;
}

.sidebar-menu-toolbar__toggle-box i {
  font-size: 1rem;
  color: #0000cc;
}

.sidebar-menu.sidebar-settings-flyout-open {
  overflow: visible;
}

.sidebar-menu::-webkit-scrollbar {
  width: 6px;
}

.sidebar-menu::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-menu::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.sidebar-menu::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

.sidebar-menu:not(.sidebar-expanded) .sidebar-menu-header .sidebar-menu-toolbar {
  justify-content: center;
  padding-left: 0.25rem;
  padding-right: 0.25rem;
}

.sidebar-menu:not(.sidebar-expanded) .menu-group--top .sidebar-menu-item {
  justify-content: center;
  padding: 0.75rem 0.25rem;
}
</style>

<style>
html.dark .sidebar-menu {
  background: #191a37;
  border-left-color: #2e2f5a;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.35);
}

html.dark .sidebar-menu .menu-divider {
  background: #2e2f5a;
}

html.dark .sidebar-menu .sidebar-menu-item {
  color: #a3a3c4;
}

html.dark .sidebar-menu .sidebar-menu-item:hover {
  background: #25274f;
  color: #e0e0f9;
}

html.dark .sidebar-menu .sidebar-menu-item.active {
  border-right-color: #6c6cf0;
  color: #8f8ff5;
  background: #1a1a40;
}

html.dark .sidebar-menu .sidebar-label {
  color: #c2c2e8;
}

html.dark .sidebar-menu::-webkit-scrollbar-thumb {
  background: #424252;
}

html.dark .sidebar-menu::-webkit-scrollbar-thumb:hover {
  background: #525262;
}
</style>
